import { Bell, Store } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useId } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { formatMoney, signedDelta } from '@/lib/whats-new/formatting';
import { revenuePath } from '@/lib/whats-new/revenue-path';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import {
  homeLeadLongest,
  homeVM,
  NO_VALUE,
  type HomeVM,
  type StoreView,
} from '@/components/whats-new/editions/estreia/view-models';
import { CountUp } from '@/components/whats-new/slides/count-up';
import { DeltaBadge } from '@/components/whats-new/slides/delta-badge';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { Rise } from '@/components/whats-new/slides/rise';
import { SampleBadge } from '@/components/whats-new/slides/sample-badge';
import {
  SLIDE_LEAD,
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { LineSkeleton } from '@/components/whats-new/slides/line-skeleton';
import { TextReserve } from '@/components/whats-new/slides/text-reserve';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

const WIDTH = 560;
const HEIGHT = 210;
const STROKE = '#6FA0FF';

/** The curve draws in 2.2 s (NEWS-32), after the card comes in. */
const DRAW = { delay: 0.5, duration: 2.2, ease: [0.45, 0.05, 0.2, 1] } as const;
const AREA = { delay: 1.6, duration: 1.2, ease: 'easeOut' } as const;
/** The markers pop (a spring with overshoot) as the curve reaches them. */
const MARKER_DELAY_S = 2.2;
const MARKER_STAGGER_S = 0.35;
/** The top campaign's label comes in at 3 s. */
const LABEL_DELAY_S = 3;
/** A point this high (in % of the chart) gets its label below it on phones. */
const HIGH_POINT_PCT = 40;
const POP = { type: 'spring', stiffness: 260, damping: 14 } as const;
/** The total's line; its skeleton keeps the same line box (NEWS-26). */
const TOTAL =
  'mt-0.5 text-[30px] font-semibold tracking-[-0.025em] max-md:text-[26px]';
/** The axis row under the chart (dates); reserved while loading. */
const AXIS = 'mt-1.5 text-[11px]';

type Chart = Extract<HomeVM['chart'], { state: 'ready' }>['value'];

function RevenueChart({ chart, store }: { chart: Chart; store: StoreView }) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const gradientId = useId();
  const path = revenuePath(chart.series, WIDTH, HEIGHT);
  const grid = [0.25, 0.5, 0.75].map((f) => path.baselineY - f * (HEIGHT - 30));
  const label = chart.label;
  const labelAt = label ? path.labelAt(label.index) : null;
  // Second marker first, so the top one is drawn on top.
  const markers = [...chart.markers].reverse();

  return (
    <div className="mt-3">
      <div className="relative">
        <svg
          data-testid="home-chart"
          role="img"
          aria-label={LL.whatsNew.editions.estreia.home.chartLabel()}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="block h-auto w-full overflow-visible"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={STROKE} stopOpacity={0.38} />
              <stop offset="1" stopColor={STROKE} stopOpacity={0} />
            </linearGradient>
          </defs>
          {grid.map((y) => (
            <line
              key={y}
              x1={0}
              x2={WIDTH}
              y1={y}
              y2={y}
              stroke="rgba(255,255,255,0.08)"
              strokeDasharray="2 4"
            />
          ))}
          <motion.path
            d={path.areaPath}
            fill={`url(#${gradientId})`}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={AREA}
          />
          <motion.path
            d={path.mainPath}
            fill="none"
            stroke={STROKE}
            strokeWidth={2.5}
            strokeLinecap="round"
            initial={reduceMotion ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={DRAW}
          />
          {/* Today, so far: the dashed last piece. */}
          <motion.path
            data-testid="home-chart-partial"
            d={path.partialPath}
            fill="none"
            stroke={STROKE}
            strokeWidth={2.5}
            strokeDasharray="4 4"
            strokeLinecap="round"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={AREA}
          />
          {markers.map((marker, k) => {
            const point = path.points[marker.index];
            if (!point) return null;
            return (
              <motion.g
                key={marker.index}
                data-testid="home-chart-marker"
                data-campaign={marker.name}
                style={{ transformBox: 'fill-box', originY: 1 }}
                initial={
                  reduceMotion ? false : { opacity: 0, scale: 0.2, y: 10 }
                }
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  ...POP,
                  delay: MARKER_DELAY_S + k * MARKER_STAGGER_S,
                }}
              >
                <line
                  x1={point.x}
                  x2={point.x}
                  y1={point.y + 6}
                  y2={HEIGHT - 22}
                  stroke="rgba(255,255,255,0.35)"
                  strokeDasharray="2 3"
                />
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={5}
                  fill="#fff"
                  stroke={STROKE}
                  strokeWidth={2.5}
                />
                <g transform={`translate(${point.x - 9} ${HEIGHT - 20})`}>
                  <rect width={18} height={18} rx={5} fill="#fff" />
                  <Bell
                    x={3}
                    y={3}
                    width={12}
                    height={12}
                    color="#155DFC"
                    strokeWidth={2.4}
                  />
                </g>
              </motion.g>
            );
          })}
        </svg>
        {label && labelAt && (
          <div
            className={cn(
              'absolute z-[5] -translate-x-1/2 -translate-y-[calc(100%+14px)]',
              // < 768px the chart is short: above a high peak the label would
              // cover the total and its badge, so it hangs below the point.
              labelAt.yPct < HIGH_POINT_PCT &&
                'max-md:translate-y-[14px] max-md:[&>*]:after:-top-1.5 max-md:[&>*]:after:bottom-auto'
            )}
            style={{ left: `${labelAt.xPct}%`, top: `${labelAt.yPct}%` }}
          >
            <motion.div
              data-testid="home-chart-label"
              className="relative flex items-center gap-2.5 whitespace-nowrap rounded-xl bg-white px-3 py-2.5 text-[#15191E] shadow-[0_18px_40px_-16px_rgba(0,0,0,0.6)] after:absolute after:-bottom-1.5 after:left-1/2 after:size-3 after:-translate-x-1/2 after:rotate-45 after:rounded-sm after:bg-white"
              initial={reduceMotion ? false : { opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...POP, delay: LABEL_DELAY_S }}
            >
              {store.iconUrl ? (
                <img
                  src={store.iconUrl}
                  alt=""
                  className="size-7 flex-none rounded-[7px]"
                />
              ) : (
                <span className="grid size-7 flex-none place-items-center rounded-[7px] bg-[#EEF3FF] text-[#0B348C]">
                  <Store
                    aria-hidden="true"
                    className="size-4"
                    strokeWidth={1.6}
                  />
                </span>
              )}
              <span className="flex flex-col">
                <b className="text-[13px]">{label.name}</b>
                <span className="text-xs text-[#4B5361]">
                  {label.result(LL)}
                </span>
              </span>
            </motion.div>
          </div>
        )}
      </div>
      <div
        data-testid="home-chart-axis"
        className={cn(AXIS, 'flex justify-between text-white/[.64]')}
      >
        {chart.axis.map((text, i) => (
          <span key={i}>{text(LL)}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * 5 · Início (NEWS-32): the 30-day app revenue counting up with its rise,
 * the chart drawing itself, the 2 top campaigns marked on their days and the
 * top one's label; the axis ends on "hoje, até agora" with the last piece
 * dashed. No campaign → the alternative text and no markers; no sales → the
 * sample series with "Exemplo".
 */
export function HomeSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const { store, chart, lead } = homeVM(useEstreiaFacts(), locale);
  const rose =
    chart.state === 'ready' && chart.value.delta
      ? signedDelta(chart.value.delta.current, chart.value.delta.previous) !==
        null
      : false;

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.home.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.home.title()} />
          </SlideTitle>
          <Rise index={2}>
            {/* The title never moves: the lead holds its longest room (FX8c). */}
            <TextReserve
              longest={homeLeadLongest(locale)(LL)}
              className={SLIDE_LEAD}
            >
              {lead.state === 'ready' ? (
                <SlideLead>{lead.value(LL)}</SlideLead>
              ) : (
                <div
                  data-testid="home-lead-loading"
                  className="flex max-w-[440px] flex-col gap-2"
                >
                  <Skeleton className="h-4 w-full bg-white/[.12]" />
                  <Skeleton className="h-4 w-3/4 bg-white/[.12]" />
                </div>
              )}
            </TextReserve>
          </Rise>
        </>
      }
      visual={
        <Rise index={1}>
          <GlassCard className="px-5 pb-3.5 pt-5 max-md:px-4 max-md:pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12.5px] text-white/[.86]">
                  {LL.whatsNew.editions.estreia.home.cardLabel()}
                </p>
                {chart.state === 'ready' ? (
                  <p data-testid="home-total" className={TOTAL}>
                    <CountUp
                      value={chart.value.total}
                      format={(value) =>
                        formatMoney(value, chart.value.currency, locale) ??
                        NO_VALUE
                      }
                    />
                  </p>
                ) : (
                  <div className={TOTAL}>
                    <LineSkeleton
                      data-testid="home-total-loading"
                      className="h-8 w-40 bg-white/[.12]"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {chart.state === 'ready' && chart.value.sample && (
                  <SampleBadge />
                )}
                {chart.state === 'ready' && chart.value.delta && rose && (
                  <span
                    data-testid="home-delta"
                    className="inline-flex flex-wrap items-center justify-end gap-x-1.5 text-xs text-white/[.64]"
                  >
                    <DeltaBadge
                      current={chart.value.delta.current}
                      previous={chart.value.delta.previous}
                    />
                    <span className="max-md:hidden">
                      {LL.whatsNew.editions.estreia.home.comparison()}
                    </span>
                  </span>
                )}
              </div>
            </div>
            {chart.state === 'ready' ? (
              <RevenueChart chart={chart.value} store={store} />
            ) : (
              <div className="mt-3">
                <Skeleton className="aspect-[560/210] w-full bg-white/[.08]" />
                <div aria-hidden="true" className={cn(AXIS, 'invisible')}>
                  0
                </div>
              </div>
            )}
          </GlassCard>
        </Rise>
      }
    />
  );
}
